import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { FilterData, IndexType } from "components/Extra"
import { Button } from "reactstrap"
import GridTableToggle from "components/Extra/GridTableToggle"
import CategoriesTable from "pages/vendor/Categories/CategoriesTable"
import { CategoriesGrid } from "pages/vendor/Categories/CategoriesGrid"
import { AddNewCategory, DeleteCategory } from "pages/vendor/Categories/Modals"
import { useNavigate, useLocation } from "react-router-dom"
import { AddNewOffer } from "pages/vendor/Offers/Modals/AddNewOffer/AddNewOffer"
import { AddNewCampaign } from "pages/vendor/Campaigns/Modals/AddNewCampaign"
import { useTranslation } from "react-i18next"
import { getAllProductCategories, toggleProductCategoryStatus } from "../../../store/product-categories/actions"

// Category type matching backend response
export type Category = {
  id: number
  aR_NAME: string
  eN_NAME: string
  uI_ORDER_ID: number
  parenT_ID: number | null
  iS_ACTIVE: boolean
  parentName: string | null
  productCount: number
}

export const Categories = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // Redux state
  const { categories, loading, error } = useSelector((state: any) => state.ProductCategories)

  const [activeTab, setActiveTab] = useState<IndexType>("list-ul")
  const [checkedCategories, setCheckedCategories] = useState<Category[]>([])
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([])
  const [openAddNewCategoryModal, setOpenAddNewCategoryModal] =
    useState<boolean>(false)
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [openAddNewOfferModal, setOpenAddNewOfferModal] =
    useState<boolean>(false)
  const [openAddNewCompaignModal, setOpenAddNewCompaignModal] =
    useState<boolean>(false)

  // Get initial page from URL query params
  const getInitialPage = useCallback(() => {
    const params = new URLSearchParams(location.search)
    const pageParam = params.get('page')
    return pageParam ? Math.max(0, parseInt(pageParam) - 1) : 0
  }, [location.search])

  // Handle page change - update URL
  const handlePageChange = useCallback((pageIndex: number) => {
    const params = new URLSearchParams(location.search)
    params.set('page', (pageIndex + 1).toString())
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
  }, [location.search, location.pathname, navigate])

  useEffect(() => {
    dispatch(getAllProductCategories())
  }, [dispatch])

  // Update filtered categories when categories from Redux change
  useEffect(() => {
    setFilteredCategories(categories)
  }, [categories])

  const isAllChecked = useMemo(
    () =>
      categories.length > 0 && checkedCategories.length === categories.length,
    [categories, checkedCategories]
  )

  const togglecheckedCategoryHandler = useCallback((category: Category) => {
    setCheckedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    )
  }, [])

  const toogleActiveCategory = useCallback(
    (category: Category) => {
      dispatch(toggleProductCategoryStatus(category.id))
    },
    [dispatch]
  )

  const toggleAllCategories = useCallback(() => {
    setCheckedCategories(isAllChecked ? [] : categories)
  }, [isAllChecked, categories])

  const addNewCategoryHandler = useCallback(() => {
    setSelectedCategory(null)
    setOpenAddNewCategoryModal(true)
  }, [])
  const editCategoryHandler = useCallback((category: Category) => {
    setSelectedCategory(category)
    setOpenAddNewCategoryModal(true)
  }, [])
  const deleteCategoryHandler = useCallback((category: Category) => {
    setSelectedCategory(category)
    setShowDeleteCategoryModal(true)
  }, [])
  const viewDetailsHandler = useCallback((id: number) => {
    navigate(`/categories/${id}`)
  }, [navigate])
  const addOfferHandler = useCallback((category: Category) => {
    setOpenAddNewOfferModal(true)
  }, [])
  const addCompaign = useCallback((category: Category) => {
    setOpenAddNewCompaignModal(true)
  }, [])

  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("categories_categories")}
        />
        <Button
          onClick={addNewCategoryHandler}
          className="bg-gradiant-blue text-capitalize btn btn-secondary border-0"
        >
          {t("categories_addNewCategory")}
        </Button>
      </div>
      <FilterData
        data={categories}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        bearerFilteredData={setFilteredCategories}
      />

      {/* Modals */}
      <AddNewCategory
        isOpen={openAddNewCategoryModal}
        toggle={() => {
          setOpenAddNewCategoryModal(false)
          setSelectedCategory(null)
        }}
        category={selectedCategory}
      />
      <DeleteCategory
        isOpen={showDeleteCategoryModal}
        toggle={() => {
          setShowDeleteCategoryModal(!showDeleteCategoryModal)
          setSelectedCategory(null)
        }}
        category={selectedCategory}
        allCategories={categories}
      />

      <AddNewOffer
        formSubmitedHandler={() => {}}
        isOpen={openAddNewOfferModal}
        toggle={() => setOpenAddNewOfferModal(false)}
      />

      <AddNewCampaign
        isOpen={openAddNewCompaignModal}
        toggle={() => setOpenAddNewCompaignModal(false)}
      />

      {activeTab == "list-ul" ? (
        <CategoriesTable
          categories={filteredCategories}
          isAllChecked={isAllChecked}
          checkedCategories={checkedCategories}
          toggleAllCategories={toggleAllCategories}
          togglecheckedCategoryHandler={togglecheckedCategoryHandler}
          toogleActiveCategory={toogleActiveCategory}
          editCategoryHandler={editCategoryHandler}
          deleteCategoryHandler={deleteCategoryHandler}
          viewDetailsHandler={viewDetailsHandler}
          addOfferHandler={addOfferHandler}
          addCompaign={addCompaign}
          initialPage={getInitialPage()}
          onPageChange={handlePageChange}
        />
      ) : (
        <CategoriesGrid
          categorys={filteredCategories}
          toggleActiveCategory={toogleActiveCategory}
          editCategoryHandler={editCategoryHandler}
          deleteCategoryHandler={deleteCategoryHandler}
          viewDetailsHandler={viewDetailsHandler}
          addOfferHandler={addOfferHandler}
          addCompaign={addCompaign}
        />
      )}
    </div>
  )
}
